/*
 * Copyright 2025 Intuition Robotics ltd
 */
import type {DB_Object} from "@intuitionrobotics/firebase";
import type {
	ApiWithBody,
	ApiWithQuery
} from "@intuitionrobotics/thunderstorm";
import type {
	BarcodeV1,
	DeviceType,
	Unit
} from "./units.js";
import type {
	Auditable,
	ObjectTS,
	TypedMap
} from "@intuitionrobotics/ts-common";
import type {Experiment} from "./experiment.js";
import type {DefaultAgents} from "./products.js";

export const CONST_ContentVersionMapKey = 'content_version_id';
export const CONST_AssignedContentVersionMapKey = 'assigned_content_version_id';
export const CONST_AssignedContentVersionLabelKey = 'assigned_content_version_label';
export const CONST_RegisteredAutomationUnitMapKey = 'registered_automation_unit_id';
export const CONST_AlertsMapKey = 'q_alerts';
export const CONST_MaintenanceMode = 'maintenance_mode';
export const CONST_StatusAlertKey = 'status_alerts'

export const CONST_CC_OrganizationKey = 'cc_organization_key'
export const CONST_CC_OrganizationType = 'cc_organization_type'
export const CONST_SP_Comment = "sp_comment"
export const CONST_SP_PresenceDetector = "use_presence_detector"
export const CONST_SP_CameraLedDisabled = "disable_camera_led"
export const CONST_SP_EnableVoiceID = "enable_voice_id"
export const CONST_SP_StoreModeEnabled = "store_mode_enabled"
export const CONST_ChattinessLevel = "chattiness_level"
// this is actually the user group, bad naming
export const CONST_KS_UserGroup = "label"
export const CONST_KS_SubUserGroup = "sub_user_group"

export type FirebasePointer = {
	projectId: string
	path: string
}

export type DB_UnitMetadata<K extends keyof RenderMapMetaData> = DB_Object & Unit & Auditable & {
	data: RenderMapMetaData[K]
	dataKey: K
	dataTarget: string
};

export const KeyDataTarget_Server = 'server';
export const KeyDataTarget_Client = 'client';

//actually maybe there already is this type?
//export type StatusMetaData = {product: string, unitId:string, som: string, tablet:string}
export type UnitAlertsInfo = Unit & {
	som?: number
	tablet?: number
	omronBtBonded?: {
		[device in DeviceType]?: OmronData
	}
};

export type OmronData = {
	timestamp: number
	bonded: boolean
}


export type RenderMapMetaData = {
	[CONST_ContentVersionMapKey]: string
	[CONST_AssignedContentVersionMapKey]: string
	[CONST_AssignedContentVersionLabelKey]: string
	[CONST_RegisteredAutomationUnitMapKey]: string
	[CONST_AlertsMapKey]: AlertObj[]
	[CONST_MaintenanceMode]: boolean
	[CONST_StatusAlertKey]: UnitAlertsInfo
	[CONST_CC_OrganizationKey]: string // Organization Key: family_doctors or elliq_care
	[CONST_CC_OrganizationType]: string // Organization Type: clinical | non_clinical | else in the future
	[CONST_SP_Comment]: string // general comment for unit = free text
	[CONST_SP_PresenceDetector]: boolean
	[CONST_SP_StoreModeEnabled]: boolean
	[CONST_SP_CameraLedDisabled]: boolean
	[CONST_SP_EnableVoiceID]: boolean
	[CONST_KS_UserGroup]: string
	[CONST_ChattinessLevel]: number
	[CONST_KS_SubUserGroup]: string
}

export type AlertObj = { key: string, timestamp?: number, remove?: boolean };

export const AlertsKey_QPulse = 'alerts--q-pulse';
export const AlertsKey_SomDown = 'alerts--som-down';
export const AlertsKey_SystemErrors = 'alerts--system-errors';
export const AlertsKey_TabletDown = 'alerts--tablet-down';

export type DB_ContentVersion = DB_Object & Auditable & FirebasePointer & {
	agents: DefaultAgents
	version: string;
	serverVersion: string;
	increment: number
	label: string
	minorVersion: string
};

export type Response_UnitIdentity = {
	id: string
	som: BarcodeV1
	tablet: BarcodeV1
	contactIds: string[]
	version?: string
	environment?: string
}

export type Response_AbTesting = {
	flags: TypedMap<string | boolean>;
	experiments: Experiment[]
}

export type ContentConfigurationData = {
	_originalVersion: string // 2.0.16-PAH_ZEVEL => 2.0.16      <- get rid of trash
	agents: DefaultAgents
	version: string // 2.0.16-${unitPair.version}
	contentVersion: string // 2.0.16-5-${unitPair.version}
	envData: TypedMap<any>
}

export type KasperoConfiguration = {
	kasperoEnv: string
	packageManagerEnv: string
};

export type UnitConfigurationData = ContentConfigurationData & {
	runtimeStatus: FirebasePointer
	versionData: FirebasePointer // the pointer to where the content version data json is stored (uploaded using firebase script)
	overrideData: FirebasePointer // the pointer to where the content version data json of a specific unit (user upload manually)
	unitData: FirebasePointer // the pointer to where the unit write it's data
	kasperoConfiguration?: KasperoConfiguration
	identity: Response_UnitIdentity
	abTesting: Response_AbTesting
	metadata:TypedMap<any>
}

export type Response_UnitConfigurationData = {
	encryptedData: string[]
}

export type Request_GetUnitConfiguration = {
	sha256: string;
	version: string;
}

export type Request_GetUnitConfigurationByUnit = {
	unitId: string;
	version: string;
	assignedContentVersionLabel?: string; // server-api passes the unit's unit_metadata value; Kaspero no longer reads the table
}

export type Request_GetContentConfiguration = {
	product: string;
	version: string;
	devUnit: boolean;
}

export type AgentEnvironment = "dev" | "staging" | "prod"
export type Request_UpsertContentVersionLinks = {
	label?: string
	agentEnv: AgentEnvironment
	appVersion: string;
	blame: string;
	serverVersion: string;
	projectId: string
	pointerToData?: FirebasePointer // deprecated
	abTestingData?: ObjectTS;
	uploadToGS?: boolean;
}

export type Request_PostUpsertContentVersionLinks = {
	contentVersionId: string
	label?: string
	success: boolean
	version: string,
	contentPath: string,
	increment?: number
}

export type Request_GetContentVersionLinks = {
	version: string
	productId: string
}

export type Request_GetContentVersionsByProduct = {
	productId?: string
	productKey?: string
	version?: string
}
export type Request_GetPlanEntriesByPrefix = {
	unitId: string
	prefix: string
}

export type PlanFunctionEntry = {
	planName: string
	functionName: string
	params: string[]
}

export type UnitConfiguration_ApiGet = ApiWithQuery<"/v1/unit-configuration/get", Response_UnitConfigurationData, Request_GetUnitConfiguration>;
export type UnitConfiguration_ApiGetProxy = ApiWithQuery<"/v1/unit-configuration/get/proxy", UnitConfigurationData, Request_GetUnitConfiguration>;
export type UnitConfiguration_ApiGetUnitProxy = ApiWithQuery<"/v1/unit-configuration/get-by-unit/proxy", UnitConfigurationData, Request_GetUnitConfigurationByUnit>;
export type ContentVersionLinks_ApiUpsert = ApiWithBody<"/v1/content-version-links/upsert", Request_UpsertContentVersionLinks, DB_ContentVersion>
export type ContentVersionLinks_ApiPostUpsert = ApiWithBody<"/v1/content-version-links/post-upsert", Request_PostUpsertContentVersionLinks, void>
export type ContentVersionLinks_ApiDelete = ApiWithBody<"/v1/content-version-links/delete-version", DB_ContentVersion, void>
export type ContentVersionLinks_ApiGet = ApiWithQuery<"/v1/content-version-links/get", DB_ContentVersion, Request_GetContentVersionLinks>
export type ContentVersionLinks_ApiGetByProduct = ApiWithQuery<"/v1/content-version-links/get-by-product", DB_ContentVersion[], Request_GetContentVersionsByProduct>
export type ContentVersionLinks_ApiGetPlansByFunctionPrefix = ApiWithQuery<"/v1/content-version-links/get-plan-names-by-ir-function-prefix-per-unit", PlanFunctionEntry[], Request_GetPlanEntriesByPrefix>

export type ContentConfiguration_ApiPost = ApiWithBody<"/v1/content-configuration/get", Request_GetContentConfiguration, ContentConfigurationData>;
